import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';
import type { AdminMemberDetail, MemberStatus } from '../hooks/useAdminUsersQuery';
import { formatMemberAddress, loginTypeLabel, nationalityLabel } from '../lib/userLabels';
import { UserStatusBadge } from './UserStatusBadge';
import { useMemberGradeDisplay } from '../hooks/useMemberTypeDisplay';
import { BusinessDocumentPreview } from '../business/components/BusinessDocumentPreview';
import { UserStatusChangeDialog } from './UserStatusChangeDialog';
import { UserUnlockButton } from './UserUnlockButton';
import { UserWithdrawalConfirmDialog } from './UserWithdrawalConfirmDialog';
import { MemberProfileEditDialog, PasswordResetCodeDialog } from './MemberCsDialogs';
import { MemberPasswordSetDialog } from './MemberPasswordSetDialog';

/**
 * QA-394 — [비밀번호 초기화]는 관리자가 새 비밀번호를 직접 정한다(be-user #37 PATCH …/password, 고도몰과 같은 흐름).
 * 인증번호 대행 발송(reset-code)은 고객이 그 번호를 넣을 화면이 없어 숨겼다(API·컴포넌트는 유지). 다시 쓰려면 true.
 */
export const MEMBER_RESET_CODE_ENABLED = false;
import { MEMBER_CS_ROLES } from '../hooks/useMemberCsMutations';
import { useAuthContext } from '@/providers/AuthProvider';
import { mobileDisplay } from '../lib/mobileDisplay';

interface UserStatusPanelProps {
  user: AdminMemberDetail;
}

const FIELD_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '140px 1fr',
  columnGap: '12px',
  rowGap: '8px',
  fontSize: '14px',
};

const fmt = (v: string | null | undefined) => (v == null || v.trim() === '' ? '—' : v);
const fmtDate = (v: string | null | undefined) => v ? new Date(v).toLocaleDateString('ko-KR') : '—';
const fmtDateTime = (v: string | null | undefined) => v ? new Date(v).toLocaleString('ko-KR') : '—';

export const UserStatusPanel = ({ user }: UserStatusPanelProps) => {
  const { t } = useTranslation('common');
  // 회원 유형 칸은 등급으로 보인다(개인·사업자 모두, 등급을 모르면 회원 유형). 유형 변경은 아래 [회원 유형] 카드.
  const memberTypeDisplay = useMemberGradeDisplay(user.memberType, user.gradeId, (user as { gradeName?: string | null }).gradeName);
  const [statusDialog, setStatusDialog] = useState<{ open: boolean; target: MemberStatus } | null>(
    null,
  );
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  // CS 정보 수정·비밀번호 초기화 — SUPER_ADMIN·OPS_ADMIN·CS_AGENT(BE 와 같은 기준). 탈퇴 회원은 수정 불가,
  // 인증번호는 활성·휴면 회원(user-service #25 — 연락처 없는 휴면 회원도 CS 가 연락처 입력 후 보낸다. 고객이
  // 새 비밀번호를 정하면 휴면도 해제된다).
  const { currentAdmin } = useAuthContext();
  const canCs = (currentAdmin?.roles ?? []).some((r) => MEMBER_CS_ROLES.includes(r));
  const [profileOpen, setProfileOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [passwordSetOpen, setPasswordSetOpen] = useState(false);

  // 휴면 → 활성은 "휴면 해제"(BE PATCH status=ACTIVE → reactivate, 휴면 해제일시 기록). 정지 해제와 같은 API 라
  // 대상 상태는 같고 문구만 다르다.
  const nextStatus: MemberStatus | null =
    user.status === 'ACTIVE'
      ? 'SUSPENDED'
      : user.status === 'SUSPENDED' || user.status === 'DORMANT'
        ? 'ACTIVE'
        : null;
  const dormancyRelease = user.status === 'DORMANT';
  const canSendResetCode = user.status === 'ACTIVE' || user.status === 'DORMANT';

  return (
    <div className="space-y-6">
      {canCs ? (
        <>
          <MemberProfileEditDialog user={user} open={profileOpen} onOpenChange={setProfileOpen} />
          {MEMBER_RESET_CODE_ENABLED ? (
            <PasswordResetCodeDialog user={user} open={resetOpen} onOpenChange={setResetOpen} />
          ) : null}
          <MemberPasswordSetDialog user={user} open={passwordSetOpen} onOpenChange={setPasswordSetOpen} />
        </>
      ) : null}
      <section
        className="rounded-xl p-5"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--content-strong)' }}>
            {t('users.sections.basicInfo')}
          </h3>
          {canCs ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={user.status === 'WITHDRAWN'}
                onClick={() => setProfileOpen(true)}
                data-testid="member-profile-edit"
              >
                {t('users.cs.edit', '정보 수정')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!canSendResetCode}
                title={
                  canSendResetCode
                    ? undefined
                    : user.status === 'PENDING_APPROVAL'
                      ? t('users.cs.resetAfterApproval', '사업자 승인 후 가능')
                      : user.status === 'WITHDRAWN'
                        ? t('users.cs.resetWithdrawn', '탈퇴 회원은 불가')
                        : t('users.cs.resetActiveOnly', '활성·휴면 회원에게만 보낼 수 있습니다.')
                }
                onClick={() => (MEMBER_RESET_CODE_ENABLED ? setResetOpen(true) : setPasswordSetOpen(true))}
                data-testid="member-password-reset"
              >
                {t('users.cs.reset', '비밀번호 초기화')}
              </Button>
            </div>
          ) : null}
        </div>
        <div style={FIELD_STYLE}>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.name')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmt(user.name)}</span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.email')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmt(user.email)}</span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.mobileNumber')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmt(mobileDisplay(user.mobileNumber))}</span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.loginId')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmt(user.loginId)}</span>
          {/* QA-399 — 로그인 유형(일반/카카오/네이버/애플). */}
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.loginType', '로그인 유형')}</span>
          <span style={{ color: 'var(--content-strong)' }} data-testid="member-login-type">
            {loginTypeLabel(t, user.signupProvider, user.linkedProviders)}
          </span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.birthDate')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmtDate(user.birthDate)}</span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.memberGrade', '회원 등급')}</span>
          <span style={{ color: 'var(--content-strong)' }} data-testid="member-grade-cell">
            {memberTypeDisplay}
          </span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.nationality')}</span>
          <span style={{ color: 'var(--content-strong)' }}>
            {nationalityLabel(t, user.nationality)}
          </span>
          {/* 회원정보 주소(고도몰 회원정보 주소 이관분) — 배송지 목록과 별개라 라벨에 "(회원정보)" 를 붙인다. */}
          <span style={{ color: 'var(--content-muted)' }}>
            {t('users.fields.memberAddress', '주소(회원정보)')}
          </span>
          <span style={{ color: 'var(--content-strong)', wordBreak: 'break-word' }} data-testid="user-member-address">
            {formatMemberAddress(user.memberPostalCode, user.memberAddress, user.memberAddressDetail)}
          </span>
          {user.memberType === 'BUSINESS' && (
            <>
              <span style={{ color: 'var(--content-muted)' }}>
                {t('users.fields.businessName')}
              </span>
              <span style={{ color: 'var(--content-strong)' }}>{fmt(user.businessName)}</span>
              <span style={{ color: 'var(--content-muted)' }}>
                {t('users.fields.businessRegNo')}
              </span>
              <span style={{ color: 'var(--content-strong)' }}>
                {fmt(user.businessRegistrationNo)}
              </span>
              <span style={{ color: 'var(--content-muted)' }}>
                {t('users.fields.businessCeoName', '대표자명')}
              </span>
              <span style={{ color: 'var(--content-strong)' }} data-testid="user-business-ceo">
                {fmt(user.businessCeoName)}
              </span>
              <span style={{ color: 'var(--content-muted)' }}>
                {t('users.fields.businessCategory', '업태')}
              </span>
              <span style={{ color: 'var(--content-strong)' }} data-testid="user-business-category">
                {fmt(user.businessCategory)}
              </span>
              <span style={{ color: 'var(--content-muted)' }}>
                {t('users.fields.businessItem', '종목')}
              </span>
              <span style={{ color: 'var(--content-strong)' }} data-testid="user-business-item">
                {fmt(user.businessItem)}
              </span>
              {/* 사업장 주소 — 회원정보 주소와 같은 형식. 상세주소 마스킹은 BE 가 unmask 권한으로 정한다. */}
              <span style={{ color: 'var(--content-muted)' }}>
                {t('users.fields.businessAddress', '사업장 주소')}
              </span>
              <span style={{ color: 'var(--content-strong)', wordBreak: 'break-word' }} data-testid="user-business-address">
                {formatMemberAddress(user.businessPostalCode, user.businessAddress, user.businessAddressDetail)}
              </span>
              {/* 사업자등록증 — 종전엔 사업자회원 승인 모달에서만 보였다(승인 후에는 다시 볼 곳이 없었다). */}
              <span style={{ color: 'var(--content-muted)' }}>
                {t('businessMembers.document.label', '사업자등록증')}
              </span>
              <div className="max-w-sm" data-testid="user-business-document">
                <BusinessDocumentPreview documentUrl={user.businessDocumentUrl} />
              </div>
            </>
          )}
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.lastLoginAt')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmtDateTime(user.lastLoginAt)}</span>
          <span style={{ color: 'var(--content-muted)' }}>
            {t('users.fields.dormancyReleasedAt', '휴면 해제일시')}
          </span>
          <span style={{ color: 'var(--content-strong)' }} data-testid="user-dormancy-released-at">
            {fmtDateTime(user.dormancyReleasedAt)}
          </span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.createdAt')}</span>
          <span style={{ color: 'var(--content-strong)' }}>{fmtDateTime(user.createdAt)}</span>
        </div>
      </section>

      <section
        className="rounded-xl p-5"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      >
        <h3 className="mb-4 text-sm font-semibold" style={{ color: 'var(--content-strong)' }}>
          {t('users.sections.statusControl')}
        </h3>
        <div className="space-y-3">
          <div style={FIELD_STYLE}>
            <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.status')}</span>
            <UserStatusBadge status={user.status} locked={user.locked} memberType={user.memberType} />
            {user.locked && user.lockedUntil && (
              <>
                <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.locked')}</span>
                <span style={{ color: 'var(--status-danger-fg)' }}>
                  {fmtDateTime(user.lockedUntil)}까지
                </span>
              </>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            {nextStatus && (
              <Button
                size="sm"
                variant={nextStatus === 'SUSPENDED' ? 'danger' : 'outline'}
                onClick={() => setStatusDialog({ open: true, target: nextStatus })}
                data-testid={dormancyRelease ? 'member-dormancy-release' : 'member-status-change'}
              >
                {dormancyRelease
                  ? t('users.actions.setStatus.DORMANT_RELEASE', '휴면 해제')
                  : t(`users.actions.setStatus.${nextStatus}`)}
              </Button>
            )}
            <UserUnlockButton userId={user.userId} locked={user.locked} />
            {user.status !== 'WITHDRAWN' && (
              <Button size="sm" variant="danger" onClick={() => setWithdrawalOpen(true)}>
                {t('users.actions.withdraw')}
              </Button>
            )}
          </div>
        </div>
      </section>

      <section
        className="rounded-xl p-5"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      >
        <h3 className="mb-4 text-sm font-semibold" style={{ color: 'var(--content-strong)' }}>
          {t('users.sections.marketing')}
        </h3>
        <div style={FIELD_STYLE}>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.marketingEmail')}</span>
          <span style={{ color: 'var(--content-strong)' }}>
            {user.marketingEmailOptIn ? t('users.optIn.yes') : t('users.optIn.no')}
          </span>
          <span style={{ color: 'var(--content-muted)' }}>{t('users.fields.marketingSms')}</span>
          <span style={{ color: 'var(--content-strong)' }}>
            {user.marketingSmsOptIn ? t('users.optIn.yes') : t('users.optIn.no')}
          </span>
        </div>
      </section>

      {statusDialog && (
        <UserStatusChangeDialog
          open={statusDialog.open}
          onOpenChange={(o) => setStatusDialog((s) => (s ? { ...s, open: o } : null))}
          userId={user.userId}
          targetStatus={statusDialog.target}
          dormancyRelease={dormancyRelease}
          onSuccess={() => setStatusDialog(null)}
        />
      )}

      <UserWithdrawalConfirmDialog
        open={withdrawalOpen}
        onOpenChange={setWithdrawalOpen}
        userId={user.userId}
      />
    </div>
  );
};
